import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, ChevronLeft, ChevronRight, Download } from "lucide-react";

import Header from "../components/Header";
import Badge from "../components/Badge";
import BlueButton from "../components/BlueButton";

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const rowperpage = 20;
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [remark, setRemark] = useState("");

  const authUserString = sessionStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const loginUserCode = authUser?.org_code;

  // ✅ Fetch invoices from API
  const fetchInvoices = async (pageNo = 1) => {
    try {
      setLoading(true);
      const url = `https://goelectronix.in/api/v1/zoho/get-invoice-summery?page=${pageNo}&per_page=${rowperpage}&org_code=${loginUserCode}`;
      const res = await axios.get(url);

      const data = res.data?.invoices?.data || [];
      const pagination = res.data?.invoices?.pagination || {};

      const formatted = data.map((item) => ({
        invoice: item.invoice_number,
        retailer: item.org_code,
        retailerName: item.org_name,
        key: item.product_type || "N/A",
        keyrate: item.invoice_amount || "0",
        amount: item.invoice_amount || 0,
        pending: item.balance_amount || 0,
        date: item.invoice_date || "-",
        status: item.invoice_status || "N/A",
        remark: item.remarks || "",
        url: item.invoice_url || "",
      }));

      setRows(formatted);
      setTotalPages(pagination.total_pages || 1);
    } catch (err) {
      console.error(err);
      setError("Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(page);
  }, [page]);

  // ✅ Pagination Handlers
  const handleNext = () => {
    if (page < totalPages) setPage((p) => p + 1);
  };

  const handlePrev = () => {
    if (page > 1) setPage((p) => p - 1);
  };

  // ✅ Handle Modal Open
  const openModal = (row) => {
    setSelectedRow(row);
    setPaidAmount(row.pending);
    setPaymentDate(new Date());
    setRemark(row.remark || "");
    setShowModal(true);
  };

  // ✅ Handle Paid Submit
  const handleSubmit = () => {
    setRows((prev) =>
      prev.map((r) =>
        r.invoice === selectedRow.invoice
          ? {
              ...r,
              status: "Paid",
              pending: 0,
              amount: Number(paidAmount),
              date: paymentDate.toISOString().split("T")[0],
              remark: remark,
            }
          : r
      )
    );
    setShowModal(false);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-40 text-gray-500">
        Loading invoices...
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-40 text-red-500">
        {error}
      </div>
    );

  return (
    <div className="space-y-3 sm:space-y-4">
      <Header title="Payments" />

      <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Sr No.</th>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Organization</th>
              <th className="px-4 py-3">Key Type</th>
              <th className="px-4 py-3">Invoice Amt</th>
              <th className="px-4 py-3">Pending</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Remark</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((r, index) => (
                <tr key={r.invoice} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">
                    {(page - 1) * rowperpage + index + 1}
                  </td>
                  <td className="px-4 py-3">{r.invoice}</td>
                  <td className="px-4 py-3">
                    {r.retailerName}
                    <br />
                    <span className="text-xs text-gray-500">
                      ({r.retailer})
                    </span>
                  </td>
                  <td className="px-4 py-3">{r.key}</td>
                  <td className="px-4 py-3">₹ {r.amount}</td>
                  <td className="px-4 py-3">₹ {r.pending}</td>
                  <td className="px-4 py-3">{r.date}</td>
                  <td className="px-4 py-3">
                    <Badge
                      label={r.status}
                      color={
                        r.status === "paid"
                          ? "green"
                          : r.status === "unpaid"
                          ? "red"
                          : r.status === "overdue"
                          ? "yellow"
                          : "blue"
                      }
                    />
                  </td>
                  <td className="px-4 py-3">{r.remark || "-"}</td>
                  <td className="px-4 py-3">
  {r.url ? (
    <BlueButton
      color="primary"
      size="sm"
      href={r.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1"
    >
      <Download className="w-4 h-4" />
      Download
    </BlueButton>
  ) : (
    <span className="text-gray-400">N/A</span>
  )}
</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-6 text-gray-500">
                  No invoices found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination Controls */}
      <div className="flex justify-between items-center text-sm text-gray-600">
        <button
          onClick={handlePrev}
          disabled={page === 1}
          className={`flex items-center gap-1 rounded-md border px-3 py-1 transition ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-blue-50 text-blue-600 border-blue-200"
          }`}
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>

        <span>
          Page <strong>{page}</strong> of <strong>{totalPages}</strong>
        </span>

        <button
          onClick={handleNext}
          disabled={page === totalPages}
          className={`flex items-center gap-1 rounded-md border px-3 py-1 transition ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-blue-50 text-blue-600 border-blue-200"
          }`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
